/*
 * File: FollowupLinks.js
 * Project: mp-mobile-app
 *
 * Created by jeddumire on 11/05/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays and styles the list of patient followups
 */
import React, { useState, useEffect } from "react";
import Api from "./PatientAppointmentApi";
import PatientIdStore from "../store/PatientIdStore";
import UIProgress from "components/ui/UIProgress";
import UINotFound from "components/ui/UINotFound";
import DateFormatter from "utils/DateFormatter";
import { ListItemText } from "@material-ui/core";
import UILinkList from "components/ui/UILinkList";
import { Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { parseISO } from "date-fns";

const styles = theme => ({
  root: {
    position: "sticky",
    zIndex: 5
  },
  dateFrequency: {
    fontWeight: 500
  },
  infoList: {
    fontWeight: 400,
    color: theme.palette.common.black
  }
});

const FollowupLinks = ({ classes }) => {
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);
  const patientId = PatientIdStore.getId();

  useEffect(() => {
    const fetchData = async () => {
      const followUps = await Api.getFollowUps(patientId);
      setFollowups(followUps);
      setLoading(false);
    };
    fetchData();
  }, [patientId]);

  const tiedAppointmentLink = followup => {
    if (!followup.scheduled) return "";
    return `/appointments/${followup.appointmentId}`;
  };

  const followupListItem = followup => (
    <ListItemText
      primary={formatPrimary(followup)}
      primaryTypographyProps={primaryColor(followup)}
      secondary={formatSecondary(followup)}
    />
  );

  const formatPrimary = followup =>
    `${followup.type}/${followup.frequency} Days`;

  const primaryColor = followup => {
    const propsObj = { variant: "h6", className: classes.dateFrequency };
    //No appointment scheduled, just use regular text color
    if (!followup.scheduled) {
      return propsObj;
    } else {
      const apptDate = parseISO(followup.scheduled);
      const followupDate = parseISO(followup.followupDate);

      //Appointment scheduled before followup date, show in red
      if (apptDate < followupDate) {
        return { ...propsObj, style: { color: "red" } };
        //Appointment scheduled on or after followup date, show in green
      } else {
        return { ...propsObj, style: { color: "green" } };
      }
    }
  };

  const formatSecondary = followup => {
    const lastVisit = DateFormatter.mdy(followup.lastVisit);
    const nextVisit = DateFormatter.mdy(followup.nextVisit);
    const apptDate = followup.scheduled
      ? DateFormatter.mdy(followup.scheduled)
      : "Not Scheduled";
    return (
      <React.Fragment>
        <Typography variant="body1" className={classes.infoList}>
          <b>Next Visit:</b> {nextVisit}
          <br />
          <b>Last Visit:</b> {lastVisit}
          <br />
          <b>Appointment Date:</b> {apptDate}
        </Typography>
      </React.Fragment>
    );
  };

  const followupList = followups.map(followup => {
    return {
      primaryString: followupListItem(followup),
      url: tiedAppointmentLink(followup)
    };
  });

  if (loading) return <UIProgress />;
  if (!followups || followups.length === 0)
    return <UINotFound message="This Patient Has No Follow-Ups" />;
  return <UILinkList links={followupList} />;
};

FollowupLinks.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(FollowupLinks);
